/* "Text an agent" early-release request — 24-hour response, approved only when it fits your Level Up career goals. */
import { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { X, Unlock, Loader2, Clock3, Briefcase } from "lucide-react";

export const RELEASE_RULE = "Early releases are approved only when the money clearly moves you toward your Level Up career goals.";

export function EarlyReleaseBanner({ onOpen, pending }) {
  return (
    <div className="mt-8 rounded-2xl bg-ink text-cream p-5 sm:p-6 flex items-start justify-between gap-4 flex-wrap" data-testid="early-release-banner">
      <div className="max-w-xl">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold"><Unlock className="w-3 h-3" /> Need money released early?</div>
        <h3 className="font-serif text-xl sm:text-2xl mt-1.5">Text an agent. We answer within 24 hours.</h3>
        <p className="text-xs text-sage-light mt-1.5 inline-flex items-start gap-1.5"><Briefcase className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {RELEASE_RULE} Tell us which vault, how much, and how it levels up your career.</p>
      </div>
      {pending ? (
        <div className="inline-flex items-center gap-2 text-xs text-gold bg-gold/15 rounded-md px-3 py-2" data-testid="early-release-pending">
          <Clock3 className="w-3.5 h-3.5" /> Request waiting · reply within 24h
        </div>
      ) : (
        <button onClick={onOpen} className="inline-flex items-center gap-2 bg-gold text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-gold-deep transition-colors" data-testid="early-release-open-btn">
          <Unlock className="w-4 h-4" /> Request early release
        </button>
      )}
    </div>
  );
}

export function EarlyReleaseDialog({ onClose, onDone }) {
  const [vaults, setVaults] = useState([]);
  const [vaultId, setVaultId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/vaults").then(({ data }) => {
      const rows = (data || []).filter((v) => Number(v.balance) > 0);
      setVaults(rows); if (rows[0]) setVaultId(rows[0].id);
    }).catch(() => {});
  }, []);

  const sel = vaults.find((v) => v.id === vaultId);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/support/early-release", { vault_id: vaultId, amount: Number(amount), reason: reason.trim() });
      onDone(data);
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-cream rounded-2xl border border-sand p-8 max-w-lg w-full" data-testid="early-release-dialog">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-deep">Early release · agent review</div>
            <h3 className="font-serif text-2xl mt-1">Text an agent</h3>
          </div>
          <button type="button" onClick={onClose} className="text-sage hover:text-ink" data-testid="early-release-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-moss mt-3">{RELEASE_RULE} An agent replies within <strong>24 hours</strong>. If approved, the money moves to Spending automatically.</p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Vault</label>
            <select value={vaultId} onChange={(e) => setVaultId(e.target.value)} required className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm" data-testid="early-release-vault">
              {vaults.length === 0 && <option value="">No vaults with money yet</option>}
              {vaults.map((v) => <option key={v.id} value={v.id}>{v.name} · {fmtMoney(v.balance)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Amount</label>
            <input type="number" min="1" step="0.01" max={sel?.balance} required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={sel ? `Up to ${fmtMoney(sel.balance)}` : "0.00"}
              className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm font-mono" data-testid="early-release-amount" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">How does this level up your career?</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} required minLength={10} maxLength={4000} rows={5}
              placeholder="e.g. Paying for the certification exam that gets me into the role in my Level Up roadmap…"
              className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm resize-none" data-testid="early-release-reason" />
          </div>
        </div>
        {err && <div className="mt-3 text-sm text-clay" data-testid="early-release-error">{err}</div>}
        <button type="submit" disabled={busy || !vaultId || !(Number(amount) > 0) || reason.trim().length < 10}
          className="w-full mt-6 bg-gold text-white rounded-xl py-3 text-sm font-medium hover:bg-gold-deep disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="early-release-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Send to an agent
        </button>
      </form>
    </div>
  );
}

export function ReleaseStatusPill({ ticket }) {
  if (ticket.kind !== "early_release" || !ticket.release) return null;
  const s = ticket.release.status;
  const cls = s === "approved" ? "bg-forest text-white" : s === "denied" ? "bg-clay text-white" : "bg-gold text-white";
  return <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded ${cls}`} data-testid={`release-status-${ticket.id}`}>Release · {s}</span>;
}
